import { yupResolver } from "@hookform/resolvers/yup";
import { Flex, Image, Text } from "@wayflyer/flyui";
import { RhfTextField } from "@wayflyer/flyui-hook-form";
import { useForm } from "react-hook-form";
import type { MessageDescriptor } from "react-intl";
import { defineMessage, useIntl } from "react-intl";
import { useCustomerAppConfig } from "undercarriage";
import * as Yup from "yup";

import { AuthCallbackPath } from "../../../api-types/connector-callbacks";
import { usePlatformConnectorProperties } from "../../../hooks/connector";
import type { ShopifyData } from "../common/connect-by-post";
import { useConnectByPost } from "../common/connect-by-post";
import { ConnectionsList } from "../common/connections";
import { OAuthErrorBanner } from "../common/oauth-error-banner";
import { MESSAGES } from "../message-definitions";

const SHOPIFY_SERVER_VALIDATION_ERRORS: Map<string, MessageDescriptor> = new Map([
  [
    "valid shopify URL hint",
    defineMessage({
      id: "funding_application: valid shopify URL hint",
      defaultMessage: "Please enter a valid Shopify store name.",
    }),
  ],
]);

const DEFAULT_SERVER_ERROR = defineMessage({
  id: "funding_application: valid shopify URL hint",
  defaultMessage: "Please enter a valid Shopify store name.",
});

const CONNECTOR_SLUG = "shopify2";

interface ShopifyConnectProps {
  postConnectPath: AuthCallbackPath;
  onInitiateConnection?: () => void;
}

export const ShopifyConnect = ({ postConnectPath, onInitiateConnection }: ShopifyConnectProps) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: connectorProperties } = usePlatformConnectorProperties(customerDomain, CONNECTOR_SLUG, postConnectPath);

  const connectByPost = useConnectByPost();

  const schema = Yup.object({
    storeName: Yup.string()
      .trim()
      .required(
        intl.formatMessage({
          id: "funding_application: valid shopify URL hint",
          defaultMessage: "Please enter a valid Shopify store name.",
        })
      )
      .matches(
        /^[a-zA-Z0-9-]{1,61}$/,
        intl.formatMessage({
          id: "funding_application: valid shopify URL hint",
          defaultMessage: "Please enter a valid Shopify store name.",
        })
      ),
  });

  const shopifyForm = useForm<ShopifyData>({
    resolver: yupResolver(schema),
  });

  const connectHandler = async (formData: ShopifyData) => {
    if (connectorProperties.postPath) {
      onInitiateConnection?.();
      const response = await connectByPost(connectorProperties.postPath, formData);
      if (response?.errorMessage) {
        if (response.errorField === "storeName") {
          shopifyForm.clearErrors();
          shopifyForm.setError("storeName", {
            type: "validate",
            message: intl.formatMessage(
              SHOPIFY_SERVER_VALIDATION_ERRORS.get(response.errorMessage) ?? DEFAULT_SERVER_ERROR
            ),
          });
        }
      }
    }
  };

  return (
    <Flex direction="column" justify="flex-start">
      <Flex direction="column" gap="8">
        <OAuthErrorBanner connectorSlug={CONNECTOR_SLUG} />
        <Text>{intl.formatMessage(MESSAGES.ecommerceDataUsage, { platform: "Shopify" })}</Text>
        <ConnectionsList
          connectorProperties={connectorProperties}
          connectForm={
            <form onSubmit={shopifyForm.handleSubmit(connectHandler)}>
              <Flex direction="row" gap="3" align="center">
                <RhfTextField
                  label={intl.formatMessage({
                    id: "funding-application: enter shopify store name",
                    defaultMessage: "Enter store name",
                  })}
                  labelIcon={
                    connectorProperties.connectionLogoSrc ? (
                      <Image src={connectorProperties.connectionLogoSrc} />
                    ) : undefined
                  }
                  form={shopifyForm}
                  name="storeName"
                  valuePrefix="https://"
                  valueSuffix=".myshopify.com"
                  disabled={shopifyForm.formState.isSubmitting}
                  inlineButton={{
                    label: intl.formatMessage({
                      id: "funding-application: login to shopify",
                      defaultMessage: "Log in to Shopify",
                    }),
                    type: "submit",
                    loading: shopifyForm.formState.isSubmitting,
                  }}
                />
              </Flex>
            </form>
          }
        />
      </Flex>
    </Flex>
  );
};
